import type { FC, ReactNode } from 'react'

import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'
import { event, tickets } from '@/constants/metric'
import { ActionButton } from '@/components/generals/action-button'
import { SectionHeading } from '@/components/generals/section-heading'

export const TicketsSection: FC = (): ReactNode => {
    return (
        <section id='tickets' aria-labelledby='tickets-title' className='scroll-mt-[96px] px-0 py-20 tab:px-[60px] tab:py-28'>
            <div className='fw-container flex flex-col items-center gap-12 tab:gap-16'>
                <SectionHeading
                    tag='Tickets'
                    title={<span id='tickets-title'>Choose your pass</span>}
                    description='Metric is a limited three-day conference. Workshop seats are capped by room size and always sell out before the standard passes do.'
                />

                <ul className='grid w-full grid-cols-1 gap-5 sm:grid-cols-2 desk:grid-cols-3'>
                    {tickets.map((ticket, index) => (
                        <li
                            key={ticket.name}
                            data-reveal='up'
                            data-reveal-delay={0.08 * index}
                            className={cn(
                                'flex h-full flex-col gap-6 rounded-[9px] border p-6 transition-colors duration-500',
                                ticket.badge
                                    ? 'border-[#454545] bg-surface'
                                    : 'border-line bg-[#0d0d0d] hover:border-[#454545]'
                            )}
                        >
                            <div className='flex items-start justify-between gap-3'>
                                <div className='flex flex-col gap-1'>
                                    <h3 className='font-sans text-[22px] font-medium leading-[1.2em] text-ink'>
                                        {ticket.name}
                                    </h3>

                                    <p className='font-body text-[14px] leading-[1.4em] text-ink-mute'>
                                        {ticket.note}
                                    </p>
                                </div>

                                {ticket.badge ? (
                                    <span className='shrink-0 rounded-[9px] border border-line bg-[#0d0d0d] px-2.5 py-1 font-sans text-[12px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink'>
                                        {ticket.badge}
                                    </span>
                                ) : null}
                            </div>

                            <p className='font-heading text-[43px] font-medium leading-[1em] tracking-[-0.04em] text-ink tab:text-[54px]'>
                                {ticket.price}
                            </p>

                            <ul className='flex flex-1 flex-col gap-3 border-t border-line pt-6'>
                                {ticket.features.map((feature) => (
                                    <li key={feature} className='flex items-start gap-3'>
                                        <Check aria-hidden='true' className='mt-[3px] size-4 shrink-0 text-ink-mute' />

                                        <span className='font-body text-[15px] leading-[1.4em] text-ink-soft'>
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <ActionButton
                                href={event.ticketsUrl}
                                variant={ticket.badge ? 'solid' : 'outline'}
                                block
                            >
                                Get your pass
                            </ActionButton>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
